import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

interface PremiumActivationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  reportName: string;
  nextStage: string | null;
  currentTracked: number;
  isLoading?: boolean;
  showStatusChange?: boolean;
}

const stepBadge =
  "mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border bg-card text-[11px] font-medium tabular-nums text-muted-foreground";

export const PremiumActivationDialog = ({
  open,
  onClose,
  onConfirm,
  reportName,
  nextStage,
  currentTracked,
  isLoading = false,
  showStatusChange = false,
}: PremiumActivationDialogProps) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  const newTrackedCount = currentTracked + 1;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mark this deal premium?</DialogTitle>
          <DialogDescription>
            Premium reports are only available on premium deals.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">This will:</p>
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-muted/30">
            {showStatusChange && nextStage && (
              <div className="flex items-start gap-3 px-3 py-2.5">
                <span className={stepBadge}>1</span>
                <p className="text-sm text-muted-foreground">
                  Change deal status to{" "}
                  <span className="font-medium text-foreground">
                    {nextStage}
                  </span>
                </p>
              </div>
            )}
            <div className="flex items-start gap-3 px-3 py-2.5">
              <span className={stepBadge}>
                {showStatusChange && nextStage ? "2" : "1"}
              </span>
              <p className="text-sm text-muted-foreground">
                Increase your premium deals from{" "}
                <span className="font-mono tabular-nums text-foreground">
                  {currentTracked}
                </span>{" "}
                to{" "}
                <span className="font-mono tabular-nums text-foreground">
                  {newTrackedCount}
                </span>
              </p>
            </div>
            <div className="flex items-start gap-3 px-3 py-2.5">
              <span className={stepBadge}>
                {showStatusChange && nextStage ? "3" : "2"}
              </span>
              <p className="text-sm text-muted-foreground">
                Generate the{" "}
                <span className="font-medium text-foreground">{reportName}</span>{" "}
                report
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Marking premium...
              </>
            ) : (
              "Mark Premium & Generate Report"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
